import { Crisp } from 'crisp-sdk-web'
import { abrirChatCrisp } from './apresentacaoDoCrisp'

/**
 * A passagem da conversa da Nina para o suporte no Crisp.
 *
 * Tudo acontece no navegador, com a identidade que `atualizarUsuarioCrisp` já
 * definiu: o servidor nunca fala com o Crisp. O resumo vai como mensagem DO
 * CLIENTE — é ela que o Hugo responde —, e o id da conversa da Nina e o evento
 * ficam no inbox para o operador, sem depender de configuração no painel.
 */

export const EVENTO_DE_ENCAMINHAMENTO = 'assistente:handoff'
const MASCARA = '[dado omitido]'

// CPF (3.3.3-2) e PIS (3.5.2-1), com ou sem pontuação. Onze dígitos colados
// também caem no padrão do CPF — um telefone sai mascarado, e é aceitável:
// a máscara é rede de segurança, e o prompt já pede para não pôr documento.
const CPF = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g
const PIS = /\b\d{3}\.?\d{5}\.?\d{2}-?\d\b/g

export interface PedidoDeEncaminhamento {
  resumo: string
  sessaoId: string
  motivo: string
}

export interface ApiDeEncaminhamentoCrisp {
  abrir: () => Promise<boolean>
  enviarMensagem: (texto: string) => void
  definirDados: (dados: Record<string, string>) => void
  registrarEvento: (nome: string, dados: Record<string, string>) => void
}

export function mascararDadosPessoais (texto: string): string {
  return texto.replace(CPF, MASCARA).replace(PIS, MASCARA)
}

export async function encaminharAoSuporteCom (
  api: ApiDeEncaminhamentoCrisp,
  pedido: PedidoDeEncaminhamento
): Promise<boolean> {
  const resumo = mascararDadosPessoais(pedido.resumo).trim()
  if (!resumo) return false
  // Abrir antes de enviar: com o widget fechado ou oculto, a mensagem iria
  // para uma conversa que a pessoa não está vendo.
  if (!await api.abrir()) return false
  try {
    api.enviarMensagem(resumo)
  } catch {
    return false
  }
  try {
    api.definirDados({ assistente_sessao_id: pedido.sessaoId })
    api.registrarEvento(EVENTO_DE_ENCAMINHAMENTO, { motivo: pedido.motivo })
  } catch {
    // Contexto do operador é extra: a mensagem da pessoa já chegou.
  }
  return true
}

const apiDoCrisp: ApiDeEncaminhamentoCrisp = {
  abrir: () => abrirChatCrisp(),
  enviarMensagem: texto => Crisp.message.send('text', texto),
  definirDados: dados => Crisp.session.setData(dados),
  registrarEvento: (nome, dados) => Crisp.session.pushEvent(nome, dados)
}

export function encaminharAoSuporte (pedido: PedidoDeEncaminhamento): Promise<boolean> {
  return encaminharAoSuporteCom(apiDoCrisp, pedido)
}

export interface HostDoEncaminhamento {
  minimizarAssistente: () => void
  reabrirAssistente: () => void
  aguardarRenderizacao: () => Promise<void>
  encaminhar: (pedido: PedidoDeEncaminhamento) => Promise<boolean>
}

/**
 * O Crisp não abre com a conversa da Nina à mostra (`ApresentacaoDoCrisp`).
 * Minimizar e esperar a tela é o que libera a abertura; se ela falhar, a Nina
 * volta, porque é nela que está o cartão com o resumo para copiar.
 */
export async function encaminharSaindoDoAssistente (
  host: HostDoEncaminhamento,
  pedido: PedidoDeEncaminhamento
): Promise<boolean> {
  host.minimizarAssistente()
  let encaminhou = false
  try {
    await host.aguardarRenderizacao()
    encaminhou = await host.encaminhar(pedido)
  } catch {
    encaminhou = false
  }
  if (!encaminhou) host.reabrirAssistente()
  return encaminhou
}
